import { Image } from 'antd';
import { RcFile } from 'antd/es/upload';
import { Upload, UploadProps } from 'antd/lib';
import styled from 'styled-components';
import { getBase64 } from 'utils/getBase64';
import toast from 'utils/toast';

interface Props {
  value?: string;
  onChange?: (value: string) => void;
}

const UploadImage = ({ value, onChange }: Props) => {
  const props: UploadProps = {
    name: 'file',
    maxCount: 1,
    showUploadList: false,
    beforeUpload: () => false,
    onChange: async (info) => {
      try {
        const convertToBase64 = await getBase64(info.file as RcFile);
        onChange?.(convertToBase64);
      } catch (error) {
        toast.error('Failed to upload image');
      }
    },
  };

  return (
    <BaseContainer>
      <Upload {...props}>
        {value ? (
          <Image src={value} preview={false} width={240} className='image' />
        ) : (
          <div className='image-container-wrapper'>
            <div className='image-container'>
              <DefaultImage />
            </div>
            <span className='image-label'>Upload Image</span>
          </div>
        )}
        <div>
          <button className='btn-change' type='button'>
            <IcUpload />
            <span className='label'>Change Image</span>
          </button>
        </div>
      </Upload>
    </BaseContainer>
  );
};

export default UploadImage;

const BaseContainer = styled.div`
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  border-radius: 4px;
  cursor: pointer;
  .image-container-wrapper {
    background: #fff;
    width: 240px;
    height: 240px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    align-self: stretch;
    border-radius: 8px;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.25);
  }

  .image {
    border-radius: 8px;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.25);
  }
  .image-container {
    width: 56px;
    height: 56px;
    aspect-ratio: 1/1;
  }
  .image-label {
    color: #505050;
    text-align: center;
    font-family: Poppins;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }

  .btn-change {
    display: flex;
    height: 48px;
    padding: 0px 24px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    align-self: stretch;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
    &:hover {
      background: #f0f0f0;
    }

    .label {
      color: #2d6772;
      text-align: center;
      font-family: Poppins;
      font-size: 20px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }
  }
`;

const DefaultImage = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='57' height='57' viewBox='0 0 57 57' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M12.626 10.0833C11.3373 10.0833 10.2926 11.128 10.2926 12.4166V45.0833C10.2926 46.0838 10.9224 46.9373 11.8072 47.2689L36.6427 22.4334C37.5539 21.5222 39.0313 21.5222 39.9426 22.4334L47.626 30.1168V12.4166C47.626 11.128 46.5813 10.0833 45.2926 10.0833H12.626ZM52.2926 35.7477V12.4166C52.2926 8.55063 49.1586 5.41663 45.2926 5.41663H12.626C8.75998 5.41663 5.62598 8.55063 5.62598 12.4166V45.0833C5.62598 48.9493 8.75998 52.0833 12.626 52.0833H45.2926C49.1586 52.0833 52.2926 48.9493 52.2926 45.0833V35.7522C52.2926 35.7507 52.2926 35.7492 52.2926 35.7477ZM47.626 36.7165L38.2926 27.3831L18.2591 47.4166H45.2926C46.5813 47.4166 47.626 46.372 47.626 45.0833V36.7165ZM20.7926 19.4166C20.1483 19.4166 19.626 19.939 19.626 20.5833C19.626 21.2276 20.1483 21.75 20.7926 21.75C21.437 21.75 21.9593 21.2276 21.9593 20.5833C21.9593 19.939 21.437 19.4166 20.7926 19.4166ZM14.9593 20.5833C14.9593 17.3616 17.571 14.75 20.7926 14.75C24.0143 14.75 26.626 17.3616 26.626 20.5833C26.626 23.805 24.0143 26.4166 20.7926 26.4166C17.571 26.4166 14.9593 23.805 14.9593 20.5833Z'
      fill='#777D7C'
    />
  </svg>
);

const IcUpload = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='24' height='23' viewBox='0 0 24 23' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M5.85135 5.59818C7.14847 4.30106 8.75017 3.50062 10.4256 3.1937L10.0076 0.912231C7.88406 1.30126 5.85176 2.31759 4.21127 3.95809C-0.0912554 8.26061 -0.0912563 15.2364 4.21127 19.5389L2.23151 21.5187H7.83111V15.9191L5.85135 17.8988C2.45462 14.5021 2.45462 8.9949 5.85135 5.59818ZM21.7698 1.98039H16.1702V7.57999L18.1499 5.60023C21.5467 8.99696 21.5467 14.5041 18.1499 17.9009C16.8528 19.198 15.2511 19.9984 13.5757 20.3054L13.9937 22.5868C16.1172 22.1978 18.1495 21.1815 19.79 19.541C24.0925 15.2384 24.0925 8.26267 19.79 3.96015L21.7698 1.98039Z'
      fill='#2D6772'
    />
  </svg>
);
